import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, money, formatApiErrorDetail, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { Heart, LogOut, ExternalLink, Plus, Pencil, Trash2, X, FolderHeart, BookOpen, ListChecks, Settings2, Mail, Images, ShoppingBag, Users, Package, ClipboardList, Search, Download, Sprout, Lightbulb, LayoutDashboard, Inbox, UserCog, ShieldAlert, HeartHandshake, KeyRound } from "lucide-react";

const TABS = [
  { id: "overview", label: "Dashboard", icon: LayoutDashboard },
  { id: "projects", label: "Projects", icon: FolderHeart, perm: "projects" },
  { id: "stories", label: "Stories", icon: BookOpen, perm: "stories" },
  { id: "transparency", label: "Transparency", icon: ListChecks, perm: "transparency" },
  { id: "supporters", label: "Supporters", icon: Users, perm: "supporters" },
  { id: "gallery", label: "Gallery", icon: Images, perm: "gallery" },
  { id: "mercymade", label: "Mercy Made", icon: ShoppingBag, perm: "mercymade" },
  { id: "shop", label: "Store", icon: Package, perm: "shop" },
  { id: "community", label: "Community", icon: Sprout, perm: "community" },
  { id: "applications", label: "Applications", icon: ClipboardList, perm: "applications" },
  { id: "contacts", label: "Contacts", icon: Inbox, perm: "contacts" },
  { id: "abe", label: "A Better Ethiopia", icon: Lightbulb, perm: "abe" },
  { id: "volunteers", label: "Volunteers", icon: HeartHandshake, perm: "volunteers" },
  { id: "employees", label: "Employees", icon: UserCog, sa: true },
  { id: "security", label: "Security", icon: ShieldAlert, sa: true },
  { id: "settings", label: "Social & Settings", icon: Settings2, sa: true },
  { id: "newsletter", label: "Subscribers", icon: Mail, sa: true },
];

const EMP_PERMS = [["projects", "Projects"], ["stories", "Stories"], ["transparency", "Transparency"], ["supporters", "Supporters"], ["gallery", "Gallery"], ["mercymade", "Mercy Made"], ["shop", "Store"], ["community", "Community"], ["applications", "Applications"], ["contacts", "Contacts"], ["abe", "A Better Ethiopia"], ["newsletter", "Subscribers"], ["volunteers", "Volunteers"]];

const CATS = ["Protect", "Educate", "Empower"];

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
const inputCls = "w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4" onClick={onClose} data-testid="admin-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl font-admin" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
          <h3 className="text-lg font-bold text-[#0A0A0A]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-black" data-testid="modal-close"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label, testId }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testId} className="flex items-center gap-2 text-sm font-medium text-[#0A0A0A]">
      <span className={`w-10 h-6 rounded-full transition-colors relative ${checked ? "bg-[#16A34A]" : "bg-gray-300"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${checked ? "left-4.5 translate-x-0" : "left-0.5"}`} style={{ left: checked ? "1.125rem" : "0.125rem" }} />
      </span>
      {label}
    </button>
  );
}

/* ---------- Projects ---------- */
function ProjectsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/admin/projects").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const blank = { title: "", category: "Protect", description: "", goal: 0, raised: 0, image_url: "", gofundme_url: "", funded_by: "", featured: false, published: true, order: items.length + 1 };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...editing, goal: Number(editing.goal) || 0, raised: Number(editing.raised) || 0, order: Number(editing.order) || 0 };
    try {
      if (editing.id) await api.put(`/admin/projects/${editing.id}`, body);
      else await api.post("/admin/projects", body);
      toast.success("Project saved");
      setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/admin/projects/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-[#0A0A0A]">Projects <span className="text-gray-400 font-normal">({items.length})</span></h2>
        <button onClick={() => setEditing(blank)} data-testid="add-project-btn" className="bg-[#0A0A0A] text-white rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-gray-800"><Plus size={16} /> New Project</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => {
          const funded = p.goal > 0 && p.raised >= p.goal;
          return (
            <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`admin-project-${p.id}`}>
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-[#52525B]">{p.category}</span>
                <div className="flex gap-1">
                  {!p.published && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Hidden</span>}
                  {funded ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Funded</span> : <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Needs</span>}
                </div>
              </div>
              <h3 className="font-semibold text-[#0A0A0A] mb-1">{p.title}</h3>
              <p className="text-sm text-[#52525B] mb-2">{money(p.raised)} / {money(p.goal)}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(p)} data-testid={`edit-project-${p.id}`} className="flex-1 border border-gray-200 rounded-md py-1.5 text-sm inline-flex items-center justify-center gap-1 hover:bg-gray-50"><Pencil size={13} /> Edit</button>
                <button onClick={() => remove(p.id)} data-testid={`delete-project-${p.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Project" : "New Project"} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            <Field label="Title"><input required className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="project-title" /></Field>
            <Field label="Category">
              <select className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="project-category">
                {CATS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Description"><textarea className={inputCls} rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="project-description" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Goal ($)"><input type="number" className={inputCls} value={editing.goal} onChange={(e) => setEditing({ ...editing, goal: e.target.value })} data-testid="project-goal" /></Field>
              <Field label="Raised ($)"><input type="number" className={inputCls} value={editing.raised} onChange={(e) => setEditing({ ...editing, raised: e.target.value })} data-testid="project-raised" /></Field>
            </div>
            <Field label="Image"><ImageUpload value={editing.image_url} onChange={(url) => setEditing({ ...editing, image_url: url })} testId="project-image" /></Field>
            <Field label="GoFundMe URL (optional — defaults to global)"><input className={inputCls} value={editing.gofundme_url} onChange={(e) => setEditing({ ...editing, gofundme_url: e.target.value })} placeholder="https://gofund.me/..." data-testid="project-gofundme" /></Field>
            <Field label="Funded by (optional credit line)"><input className={inputCls} value={editing.funded_by || ""} onChange={(e) => setEditing({ ...editing, funded_by: e.target.value })} placeholder="e.g. The Alemu Family" data-testid="project-funded-by" /></Field>
            <Field label="Display order"><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} data-testid="project-order" /></Field>
            <div className="flex gap-6 my-4">
              <Toggle checked={editing.featured} onChange={(v) => setEditing({ ...editing, featured: v })} label="Featured on home" testId="project-featured" />
              <Toggle checked={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} label="Published" testId="project-published" />
            </div>
            <button type="submit" data-testid="save-project" className="w-full bg-[#0A0A0A] text-white rounded-md py-2.5 font-medium hover:bg-gray-800">Save Project</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Stories ---------- */
function StoriesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/stories").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const blank = { title: "", subtitle: "", body: "", image_url: "", is_memorial: false, published: true, order: items.length + 1 };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...editing, order: Number(editing.order) || 0 };
    try {
      if (editing.id) await api.put(`/admin/stories/${editing.id}`, body);
      else await api.post("/admin/stories", body);
      toast.success("Story saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this story?")) return; await api.delete(`/admin/stories/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-[#0A0A0A]">Stories <span className="text-gray-400 font-normal">({items.length})</span></h2>
        <button onClick={() => setEditing(blank)} data-testid="add-story-btn" className="bg-[#0A0A0A] text-white rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-gray-800"><Plus size={16} /> New Story</button>
      </div>
      <div className="space-y-3">
        {items.map((s) => (
          <div key={s.id} className="bg-white border border-gray-200 rounded-lg p-4 flex justify-between items-center" data-testid={`admin-story-${s.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-[#0A0A0A]">{s.title}</h3>
                {s.is_memorial && <span className="text-xs bg-[#C1272D]/10 text-[#C1272D] px-2 py-0.5 rounded">Memorial</span>}
                {!s.published && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Hidden</span>}
              </div>
              <p className="text-sm text-[#52525B]">{s.subtitle}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(s)} data-testid={`edit-story-${s.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-sm inline-flex items-center gap-1 hover:bg-gray-50"><Pencil size={13} /> Edit</button>
              <button onClick={() => remove(s.id)} data-testid={`delete-story-${s.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Story" : "New Story"} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            <Field label="Title"><input required className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="story-title" /></Field>
            <Field label="Subtitle"><input className={inputCls} value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} data-testid="story-subtitle" /></Field>
            <Field label="Body"><textarea className={inputCls} rows={6} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="story-body" /></Field>
            <Field label="Image"><ImageUpload value={editing.image_url} onChange={(url) => setEditing({ ...editing, image_url: url })} testId="story-image" /></Field>
            <Field label="Display order"><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} data-testid="story-order" /></Field>
            <div className="flex gap-6 my-4">
              <Toggle checked={editing.is_memorial} onChange={(v) => setEditing({ ...editing, is_memorial: v })} label="Mercy's memorial" testId="story-memorial" />
              <Toggle checked={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} label="Published" testId="story-published" />
            </div>
            <button type="submit" data-testid="save-story" className="w-full bg-[#0A0A0A] text-white rounded-md py-2.5 font-medium hover:bg-gray-800">Save Story</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Transparency ---------- */
function TransparencyTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/transparency").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const blank = { title: "", body: "", amount: "", date: new Date().toISOString().slice(0, 10), image_url: "", published: true };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...editing, amount: editing.amount === "" ? null : Number(editing.amount), date: new Date(editing.date).toISOString() };
    try {
      if (editing.id) await api.put(`/admin/transparency/${editing.id}`, body);
      else await api.post("/admin/transparency", body);
      toast.success("Update saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this update?")) return; await api.delete(`/admin/transparency/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-[#0A0A0A]">Transparency Updates <span className="text-gray-400 font-normal">({items.length})</span></h2>
        <button onClick={() => setEditing(blank)} data-testid="add-transparency-btn" className="bg-[#0A0A0A] text-white rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-gray-800"><Plus size={16} /> New Update</button>
      </div>
      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-gray-200 rounded-lg p-4 flex justify-between items-center" data-testid={`admin-transparency-${t.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-[#0A0A0A]">{t.title}</h3>
                {t.amount != null && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{money(t.amount)}</span>}
                {!t.published && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Hidden</span>}
              </div>
              <p className="text-sm text-[#52525B]">{new Date(t.date).toLocaleDateString()}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing({ ...t, date: t.date.slice(0, 10), amount: t.amount ?? "" })} data-testid={`edit-transparency-${t.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-sm inline-flex items-center gap-1 hover:bg-gray-50"><Pencil size={13} /> Edit</button>
              <button onClick={() => remove(t.id)} data-testid={`delete-transparency-${t.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Update" : "New Update"} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            <Field label="Title"><input required className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="transparency-title" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Amount ($) — optional"><input type="number" className={inputCls} value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} data-testid="transparency-amount" /></Field>
              <Field label="Date"><input type="date" className={inputCls} value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid="transparency-date" /></Field>
            </div>
            <Field label="Body"><textarea className={inputCls} rows={4} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="transparency-body" /></Field>
            <Field label="Image (optional)"><ImageUpload value={editing.image_url} onChange={(url) => setEditing({ ...editing, image_url: url })} testId="transparency-image" /></Field>
            <div className="my-4"><Toggle checked={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} label="Published" testId="transparency-published" /></div>
            <button type="submit" data-testid="save-transparency" className="w-full bg-[#0A0A0A] text-white rounded-md py-2.5 font-medium hover:bg-gray-800">Save Update</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Settings ---------- */
function SettingsTab() {
  const [s, setS] = useState(null);
  const [anthemBusy, setAnthemBusy] = useState(false);
  const anthemRef = useRef();
  useEffect(() => { api.get("/settings").then(({ data }) => setS({ ...data, instagram_fallback: data.instagram_fallback || [] })).catch(() => {}); }, []);

  const uploadAnthem = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnthemBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const type = (data.content_type || file.type || "").startsWith("video") ? "video" : "audio";
      setS((prev) => ({ ...prev, anthem_url: data.url, anthem_type: type }));
      toast.success("Anthem uploaded — remember to Save Settings");
    } catch (err) {
      toast.error("Upload failed");
    } finally { setAnthemBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  if (!s) return null;

  const setFallback = (i, key, val) => {
    const fb = [...s.instagram_fallback]; fb[i] = { ...fb[i], [key]: val }; setS({ ...s, instagram_fallback: fb });
  };

  return (
    <form onSubmit={save} className="max-w-2xl">
      <h2 className="text-xl font-bold text-[#0A0A0A] mb-6">Social & Global Settings</h2>
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <Field label="GoFundMe URL (used everywhere)"><input className={inputCls} value={s.gofundme_url || ""} onChange={(e) => setS({ ...s, gofundme_url: e.target.value })} data-testid="settings-gofundme" /></Field>
        <Field label="YouTube video link (latest video embed)"><input className={inputCls} value={s.youtube_url || ""} onChange={(e) => setS({ ...s, youtube_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." data-testid="settings-youtube" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Instagram URL"><input className={inputCls} value={s.social_instagram || ""} onChange={(e) => setS({ ...s, social_instagram: e.target.value })} data-testid="settings-instagram" /></Field>
          <Field label="YouTube channel"><input className={inputCls} value={s.social_youtube || ""} onChange={(e) => setS({ ...s, social_youtube: e.target.value })} data-testid="settings-youtube-channel" /></Field>
          <Field label="X (Twitter)"><input className={inputCls} value={s.social_x || ""} onChange={(e) => setS({ ...s, social_x: e.target.value })} data-testid="settings-x" /></Field>
          <Field label="Facebook URL"><input className={inputCls} value={s.social_facebook || ""} onChange={(e) => setS({ ...s, social_facebook: e.target.value })} data-testid="settings-facebook" /></Field>
        </div>
        <Field label="Contact email"><input className={inputCls} value={s.contact_email || ""} onChange={(e) => setS({ ...s, contact_email: e.target.value })} data-testid="settings-contact" /></Field>
        <Field label="Active monthly supporters (shown on the Refugium Village section once above 0)"><input type="number" min="0" className={inputCls} value={s.monthly_supporters ?? 0} onChange={(e) => setS({ ...s, monthly_supporters: Number(e.target.value) || 0 })} data-testid="settings-monthly-supporters" /></Field>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 mt-5">
        <h3 className="font-semibold text-[#0A0A0A] mb-1">Instagram fallback tiles</h3>
        <p className="text-sm text-[#52525B] mb-4">Manual images shown in the social grid so it never breaks. Add up to 3.</p>
        {[0, 1, 2].map((i) => {
          const f = s.instagram_fallback[i] || { image_url: "", link: "" };
          return (
            <div key={i} className="border border-gray-100 rounded-md p-3 mb-3" data-testid={`fallback-${i}`}>
              <ImageUpload value={f.image_url} onChange={(url) => setFallback(i, "image_url", url)} testId={`fallback-img-${i}`} />
              <input className={`${inputCls} mt-2`} placeholder="Link (optional)" value={f.link || ""} onChange={(e) => setFallback(i, "link", e.target.value)} data-testid={`fallback-link-${i}`} />
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 mt-5">
        <h3 className="font-semibold text-[#0A0A0A] mb-1">Our Anthem</h3>
        <p className="text-sm text-[#52525B] mb-4">Upload the official anthem (audio or video). It appears as a floating player site-wide and on the Home, Story &amp; About pages.</p>
        <Field label="Anthem title"><input className={inputCls} value={s.anthem_title || ""} onChange={(e) => setS({ ...s, anthem_title: e.target.value })} placeholder="e.g. Refugium Ethiopia Anthem" data-testid="settings-anthem-title" /></Field>
        <div className="flex items-center gap-3 mb-3">
          <button type="button" onClick={() => anthemRef.current.click()} disabled={anthemBusy} data-testid="anthem-upload-btn" className="inline-flex items-center gap-2 border border-gray-300 rounded-md px-3 py-2 text-sm font-medium hover:bg-gray-50">
            {anthemBusy ? "Uploading…" : "Upload audio / video"}
          </button>
          <input ref={anthemRef} type="file" accept="audio/*,video/*" className="hidden" onChange={uploadAnthem} data-testid="anthem-file-input" />
          {s.anthem_url && <span className="text-xs text-green-700">Uploaded ({s.anthem_type || "media"})</span>}
          {s.anthem_url && <button type="button" onClick={() => setS({ ...s, anthem_url: "", anthem_type: "" })} className="text-xs text-red-600 hover:underline" data-testid="anthem-remove">Remove</button>}
        </div>
        {s.anthem_url && (s.anthem_type === "video"
          ? <video src={imgUrl(s.anthem_url)} controls className="w-full max-h-56 rounded-md bg-black" data-testid="anthem-preview-video" />
          : <audio src={imgUrl(s.anthem_url)} controls className="w-full" data-testid="anthem-preview-audio" />)}
      </div>

      <button type="submit" data-testid="save-settings" className="mt-6 bg-[#0A0A0A] text-white rounded-md px-6 py-2.5 font-medium hover:bg-gray-800">Save Settings</button>
    </form>
  );
}

/* ---------- Mercy Made ---------- */
function MercyMadeTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/mercy-products").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const blank = { name: "", description: "", price: "", image_url: "", order: items.length + 1, published: true };

  const save = async (e) => {
    e.preventDefault();
    if (!editing.image_url) { toast.error("Please add a product image first"); return; }
    const body = { ...editing, order: Number(editing.order) || 0 };
    try {
      if (editing.id) await api.put(`/admin/mercy-products/${editing.id}`, body);
      else await api.post("/admin/mercy-products", body);
      toast.success("Product saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/admin/mercy-products/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-[#0A0A0A]">Mercy Made Products <span className="text-gray-400 font-normal">({items.length})</span></h2>
        <button onClick={() => setEditing(blank)} data-testid="add-mercy-btn" className="bg-[#0A0A0A] text-white rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-gray-800"><Plus size={16} /> New Product</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`admin-mercy-${p.id}`}>
            <img src={imgUrl(p.image_url)} alt={p.name} className="w-full h-36 object-cover" />
            <div className="p-3">
              <h3 className="font-semibold text-[#0A0A0A] text-sm">{p.name}</h3>
              <p className="text-xs text-[#52525B] mb-2">{p.price || "—"}{!p.published && " · Hidden"}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing(p)} data-testid={`edit-mercy-${p.id}`} className="flex-1 border border-gray-200 rounded-md py-1.5 text-xs inline-flex items-center justify-center gap-1 hover:bg-gray-50"><Pencil size={12} /> Edit</button>
                <button onClick={() => remove(p.id)} data-testid={`delete-mercy-${p.id}`} className="border border-gray-200 rounded-md px-2.5 py-1.5 text-red-600 hover:bg-red-50"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Product" : "New Product"} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            <Field label="Product name"><input required className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="mercy-name" /></Field>
            <Field label="Price (free text, e.g. $12 or 'Inquire for pricing')"><input className={inputCls} value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="mercy-price" /></Field>
            <Field label="Description"><textarea className={inputCls} rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="mercy-description" /></Field>
            <Field label="Product image"><ImageUpload value={editing.image_url} onChange={(url) => setEditing({ ...editing, image_url: url })} testId="mercy-image" /></Field>
            <Field label="Display order"><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} data-testid="mercy-order" /></Field>
            <div className="my-4"><Toggle checked={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} label="Published" testId="mercy-published" /></div>
            <button type="submit" data-testid="save-mercy" className="w-full bg-[#0A0A0A] text-white rounded-md py-2.5 font-medium hover:bg-gray-800">Save Product</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Supporters ---------- */
function SupportersTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/supporters").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const blank = { name: "", amount: "", message: "", featured: false, date: new Date().toISOString().slice(0, 10), published: true };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...editing, amount: editing.amount === "" ? null : Number(editing.amount), date: new Date(editing.date).toISOString() };
    try {
      if (editing.id) await api.put(`/admin/supporters/${editing.id}`, body);
      else await api.post("/admin/supporters", body);
      toast.success("Supporter saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Remove this supporter?")) return; await api.delete(`/admin/supporters/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-[#0A0A0A]">Supporters Wall <span className="text-gray-400 font-normal">({items.length})</span></h2>
        <button onClick={() => setEditing(blank)} data-testid="add-supporter-btn" className="bg-[#0A0A0A] text-white rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-gray-800"><Plus size={16} /> Add Supporter</button>
      </div>
      <p className="text-sm text-[#52525B] mb-4">Add supporters here only as genuine gifts arrive — they appear publicly on the Transparency page wall.</p>
      <div className="space-y-3">
        {items.length === 0 ? <p className="text-gray-400 py-10 text-center border border-dashed border-gray-200 rounded-lg" data-testid="admin-supporters-empty">No supporters yet.</p> :
          items.map((s) => (
            <div key={s.id} className="bg-white border border-gray-200 rounded-lg p-4 flex justify-between items-center" data-testid={`admin-supporter-${s.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-[#0A0A0A]">{s.name}</h3>
                  {s.featured && <span className="text-xs bg-[#E0A526]/20 text-[#8a6d18] px-2 py-0.5 rounded">★ Featured</span>}
                  {s.amount != null && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{money(s.amount)}</span>}
                  {!s.published && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Hidden</span>}
                </div>
                {s.message && <p className="text-sm text-[#52525B] italic">“{s.message}”</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ ...s, date: s.date.slice(0, 10), amount: s.amount ?? "" })} data-testid={`edit-supporter-${s.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-sm inline-flex items-center gap-1 hover:bg-gray-50"><Pencil size={13} /> Edit</button>
                <button onClick={() => remove(s.id)} data-testid={`delete-supporter-${s.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Supporter" : "Add Supporter"} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            <Field label="Supporter name"><input required className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="supporter-name" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Amount ($) — optional"><input type="number" className={inputCls} value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} data-testid="supporter-amount" /></Field>
              <Field label="Date"><input type="date" className={inputCls} value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid="supporter-date" /></Field>
            </div>
            <Field label="Message / dedication — optional"><textarea className={inputCls} rows={2} value={editing.message} onChange={(e) => setEditing({ ...editing, message: e.target.value })} data-testid="supporter-message" /></Field>
            <div className="flex gap-6 my-4">
              <Toggle checked={editing.featured} onChange={(v) => setEditing({ ...editing, featured: v })} label="Supporter of the month" testId="supporter-featured" />
              <Toggle checked={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} label="Published" testId="supporter-published" />
            </div>
            <button type="submit" data-testid="save-supporter" className="w-full bg-[#0A0A0A] text-white rounded-md py-2.5 font-medium hover:bg-gray-800">Save Supporter</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Store ---------- */
function GalleryUploader({ value, onChange, testId }) {
  const imgs = value || [];
  return (
    <div>
      {imgs.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {imgs.map((u, i) => (
            <div key={i} className="relative w-16 h-16">
              <img src={imgUrl(u)} alt="" className="w-16 h-16 object-cover rounded-md border border-gray-200" />
              <button type="button" onClick={() => onChange(imgs.filter((_, x) => x !== i))} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 text-xs leading-none">×</button>
            </div>
          ))}
        </div>
      )}
      <ImageUpload value="" onChange={(url) => url && onChange([...imgs, url])} testId={testId} />
    </div>
  );
}

const INTEG_STATUSES = ["NOT CONNECTED", "CONFIGURED", "TEST MODE", "CONNECTED", "ERROR"];
function StatusDot({ status }) {
  const color = { "NOT CONNECTED": "bg-gray-300", CONFIGURED: "bg-blue-400", "TEST MODE": "bg-[#E0A526]", CONNECTED: "bg-green-500", ERROR: "bg-red-500" }[status] || "bg-gray-300";
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />;
}

function ShopSettingsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/shop/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/admin/shop/settings", s); toast.success("Store settings saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  if (!s) return null;
  const set = (k, v) => setS({ ...s, [k]: v });
  return (
    <form onSubmit={save} className="max-w-3xl" data-testid="shop-settings-panel">
      <div className="bg-[#FBF8F0] border border-[#E0A526]/40 rounded-lg p-4 mb-6 text-sm text-[#4F6B54]">
        <b>Foundation phase.</b> Live payments and fulfillment are not connected yet. Ordering stays disabled until you turn it on below — the storefront shows an "Online ordering is coming soon" message and never charges anyone.
      </div>

      <h3 className="text-sm font-bold uppercase tracking-wide text-[#0A0A0A] mb-3">🇪🇹 Ethiopia</h3>
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Payment Gateway <StatusDot status={s.eth_payment_status} /></span>
          <select value={s.eth_payment_status} onChange={(e) => set("eth_payment_status", e.target.value)} className={`${inputCls} max-w-[200px]`} data-testid="eth-payment-status">{INTEG_STATUSES.map((x) => <option key={x}>{x}</option>)}</select>
        </div>
        <Field label="Payment provider (e.g. Chapa — leave blank until chosen)"><input className={inputCls} value={s.eth_payment_provider || ""} onChange={(e) => set("eth_payment_provider", e.target.value)} data-testid="eth-payment-provider" /></Field>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Local Fulfillment Email <StatusDot status={s.eth_fulfillment_email_status} /></span>
          <select value={s.eth_fulfillment_email_status} onChange={(e) => set("eth_fulfillment_email_status", e.target.value)} className={`${inputCls} max-w-[200px]`} data-testid="eth-fulfillment-status">{INTEG_STATUSES.map((x) => <option key={x}>{x}</option>)}</select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Fulfillment notification email"><input className={inputCls} value={s.eth_fulfillment_email || ""} onChange={(e) => set("eth_fulfillment_email", e.target.value)} placeholder="team@…" data-testid="eth-fulfillment-email" /></Field>
          <Field label="Secondary notification email"><input className={inputCls} value={s.eth_secondary_email || ""} onChange={(e) => set("eth_secondary_email", e.target.value)} data-testid="eth-secondary-email" /></Field>
        </div>
        <Toggle checked={s.ordering_enabled_eth} onChange={(v) => set("ordering_enabled_eth", v)} label="Enable Ethiopia online ordering" testId="eth-ordering-toggle" />
      </div>

      <h3 className="text-sm font-bold uppercase tracking-wide text-[#0A0A0A] mb-3">🌍 International</h3>
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Payment Gateway <StatusDot status={s.intl_payment_status} /></span>
          <select value={s.intl_payment_status} onChange={(e) => set("intl_payment_status", e.target.value)} className={`${inputCls} max-w-[200px]`} data-testid="intl-payment-status">{INTEG_STATUSES.map((x) => <option key={x}>{x}</option>)}</select>
        </div>
        <Field label="Payment provider (e.g. Stripe — leave blank until chosen)"><input className={inputCls} value={s.intl_payment_provider || ""} onChange={(e) => set("intl_payment_provider", e.target.value)} data-testid="intl-payment-provider" /></Field>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Print-on-Demand Provider <StatusDot status={s.intl_pod_status} /></span>
          <select value={s.intl_pod_status} onChange={(e) => set("intl_pod_status", e.target.value)} className={`${inputCls} max-w-[200px]`} data-testid="intl-pod-status">{INTEG_STATUSES.map((x) => <option key={x}>{x}</option>)}</select>
        </div>
        <Field label="POD provider (Printful / Printify — leave blank until chosen)"><input className={inputCls} value={s.intl_pod_provider || ""} onChange={(e) => set("intl_pod_provider", e.target.value)} placeholder="printful / printify" data-testid="intl-pod-provider" /></Field>
        <Toggle checked={s.ordering_enabled_intl} onChange={(v) => set("ordering_enabled_intl", v)} label="Enable international online ordering" testId="intl-ordering-toggle" />
      </div>

      <button type="submit" data-testid="save-shop-settings" className="bg-[#0A0A0A] text-white rounded-md px-6 py-2.5 font-medium hover:bg-gray-800">Save Store Settings</button>
    </form>
  );
}

function ShopTab() {
  const [sub, setSub] = useState("products");
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const loadP = useCallback(() => api.get("/admin/shop/products").then(({ data }) => setItems(data)).catch(() => {}), []);
  const loadO = useCallback(() => api.get("/admin/shop/orders").then(({ data }) => setOrders(data)).catch(() => {}), []);
  useEffect(() => { loadP(); loadO(); }, [loadP, loadO]);
  const STATUSES = ["available", "coming_soon", "ethiopia_only", "global_only", "ethiopia_global", "made_in_ethiopia", "limited_edition", "sold_out", "discontinued"];
  const ORDER_STATUSES = ["order_received", "payment_confirmed", "in_production", "ready_to_ship", "shipped", "out_for_delivery", "delivered", "cancelled", "refunded"];
  const blank = { slug: "", name: "", category: "T-Shirts", description: "",
    available_eth: true, available_intl: true,
    price_eth: 0, inventory_eth: 0, image_eth_main: "", images_eth: [],
    price_intl: 0, currency_intl: "USD", image_global_main: "", images_global: [],
    pod_provider: "", provider_product_id: "", provider_variant_id: "",
    colors: "", sizes: "", status: "ethiopia_global", badges: [], production_location: "", material: "", care: "",
    order: items.length + 1, published: true };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...editing,
      price_intl: Number(editing.price_intl) || 0, price_eth: Number(editing.price_eth) || 0,
      inventory_eth: Number(editing.inventory_eth) || 0, order: Number(editing.order) || 0,
      colors: typeof editing.colors === "string" ? editing.colors.split(",").map((s) => s.trim()).filter(Boolean) : editing.colors,
      sizes: typeof editing.sizes === "string" ? editing.sizes.split(",").map((s) => s.trim()).filter(Boolean) : editing.sizes,
      images_eth: editing.images_eth || [], images_global: editing.images_global || [] };
    try {
      if (editing.id) await api.put(`/admin/shop/products/${editing.id}`, body);
      else await api.post("/admin/shop/products", body);
      toast.success("Product saved"); setEditing(null); loadP();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/admin/shop/products/${id}`); toast.success("Deleted"); loadP(); };
  const setOrderStatus = async (oid, status) => { await api.put(`/admin/shop/orders/${oid}`, { status }); toast.success("Order updated"); loadO(); };

  const ethOrders = orders.filter((o) => o.market === "ethiopia").length;
  const globalOrders = orders.filter((o) => o.market === "global").length;
  const cardImg = (p) => p.image_global_main || p.image_eth_main || p.images?.[0] || "";

  return (
    <div>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setSub("products")} data-testid="shop-products-tab" className={`px-4 py-2 rounded-md text-sm font-medium ${sub === "products" ? "bg-[#0A0A0A] text-white" : "border border-gray-200"}`}>Products ({items.length})</button>
        <button onClick={() => setSub("orders")} data-testid="shop-orders-tab" className={`px-4 py-2 rounded-md text-sm font-medium ${sub === "orders" ? "bg-[#0A0A0A] text-white" : "border border-gray-200"}`}>Orders ({orders.length})</button>
        <button onClick={() => setSub("settings")} data-testid="shop-settings-tab" className={`px-4 py-2 rounded-md text-sm font-medium ${sub === "settings" ? "bg-[#0A0A0A] text-white" : "border border-gray-200"}`}>Settings &amp; Integrations</button>
      </div>

      {sub === "settings" ? <ShopSettingsPanel /> : sub === "products" ? (
        <>
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-[#52525B]">Global orders: <b>{globalOrders}</b> · Ethiopia orders: <b>{ethOrders}</b></div>
            <button onClick={() => setEditing(blank)} data-testid="add-shop-product" className="bg-[#0A0A0A] text-white rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><Plus size={16} /> New Product</button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {items.map((p) => (
              <div key={p.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`admin-shop-${p.slug}`}>
                {cardImg(p) ? <img src={imgUrl(cardImg(p))} alt={p.name} className="w-full h-32 object-cover" />
                  : <div className="w-full h-32 bg-gray-100 flex items-center justify-center text-gray-400"><Package size={26} /></div>}
                <div className="p-3">
                  <h3 className="font-semibold text-sm">{p.name}</h3>
                  <p className="text-xs text-[#52525B] mb-1">{p.price_intl ? `$${p.price_intl}` : "$—"} · {p.price_eth ? `ETB ${p.price_eth}` : "ETB —"}</p>
                  <div className="flex gap-1 mb-2 text-[10px]">
                    <span className={`px-1.5 py-0.5 rounded ${p.available_eth ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>🇪🇹 {p.available_eth ? "On" : "Off"}</span>
                    <span className={`px-1.5 py-0.5 rounded ${p.available_intl ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>🌍 {p.available_intl ? "On" : "Off"}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing({ ...p, colors: (p.colors || []).join(", "), sizes: (p.sizes || []).join(", ") })} data-testid={`edit-shop-${p.slug}`} className="flex-1 border border-gray-200 rounded-md py-1.5 text-xs inline-flex items-center justify-center gap-1"><Pencil size={12} /> Edit</button>
                    <button onClick={() => remove(p.id)} data-testid={`delete-shop-${p.slug}`} className="border border-gray-200 rounded-md px-2.5 py-1.5 text-red-600"><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="space-y-3">
          {orders.length === 0 ? <p className="text-gray-400 py-10 text-center border border-dashed border-gray-200 rounded-lg" data-testid="admin-orders-empty">No orders yet.</p> :
            orders.map((o) => (
              <div key={o.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`admin-order-${o.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <p className="font-semibold text-sm">#{o.id.slice(0, 8)} · {o.name || o.email}</p>
                    <p className="text-xs text-[#52525B]">{o.market === "ethiopia" ? "🇪🇹 Ethiopia" : "🌍 Global"}{o.country ? ` · ${o.country}` : ""} · {o.items?.length || 0} item(s) · {o.currency} {o.total}</p>
                  </div>
                  <select value={o.status} onChange={(e) => setOrderStatus(o.id, e.target.value)} className="text-xs border border-gray-200 rounded-md px-2 py-1.5">
                    {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
              </div>
            ))}
        </div>
      )}

      {editing && (
        <Modal title={editing.id ? "Edit Product" : "New Product"} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name"><input required className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="sp-name" /></Field>
              <Field label="Slug (url)"><input required className={inputCls} value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="sp-slug" /></Field>
            </div>
            <Field label="Category"><input className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} /></Field>
            <Field label="Description"><textarea className={inputCls} rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Colors (comma separated)"><input className={inputCls} value={editing.colors} onChange={(e) => setEditing({ ...editing, colors: e.target.value })} /></Field>
              <Field label="Sizes (comma separated)"><input className={inputCls} value={editing.sizes} onChange={(e) => setEditing({ ...editing, sizes: e.target.value })} /></Field>
            </div>

            <div className="border-t border-gray-100 my-4 pt-4">
              <p className="text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-3">Market availability</p>
              <div className="flex gap-6">
                <Toggle checked={editing.available_eth} onChange={(v) => setEditing({ ...editing, available_eth: v })} label="🇪🇹 Ethiopia" testId="sp-avail-eth" />
                <Toggle checked={editing.available_intl} onChange={(v) => setEditing({ ...editing, available_intl: v })} label="🌍 International" testId="sp-avail-intl" />
              </div>
            </div>

            <div className="border border-[#1F4D2E]/15 rounded-lg p-4 mb-4">
              <p className="text-sm font-bold text-[#1F4D2E] mb-3">🇪🇹 Ethiopia</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="ETB price (0 = coming soon)"><input type="number" className={inputCls} value={editing.price_eth} onChange={(e) => setEditing({ ...editing, price_eth: e.target.value })} data-testid="sp-price-eth" /></Field>
                <Field label="Local inventory"><input type="number" className={inputCls} value={editing.inventory_eth} onChange={(e) => setEditing({ ...editing, inventory_eth: e.target.value })} data-testid="sp-inventory-eth" /></Field>
              </div>
              <Field label="Ethiopia main image (optional — falls back to global)"><ImageUpload value={editing.image_eth_main} onChange={(url) => setEditing({ ...editing, image_eth_main: url })} testId="sp-image-eth" /></Field>
              <Field label="Ethiopia additional images"><GalleryUploader value={editing.images_eth} onChange={(arr) => setEditing({ ...editing, images_eth: arr })} testId="sp-gallery-eth" /></Field>
            </div>

            <div className="border border-[#14241C]/15 rounded-lg p-4 mb-4">
              <p className="text-sm font-bold text-[#14241C] mb-3">🌍 International</p>
              <Field label="USD price (0 = coming soon)"><input type="number" className={inputCls} value={editing.price_intl} onChange={(e) => setEditing({ ...editing, price_intl: e.target.value })} data-testid="sp-price-intl" /></Field>
              <Field label="Global main image"><ImageUpload value={editing.image_global_main} onChange={(url) => setEditing({ ...editing, image_global_main: url })} testId="sp-image-global" /></Field>
              <Field label="Global additional images"><GalleryUploader value={editing.images_global} onChange={(arr) => setEditing({ ...editing, images_global: arr })} testId="sp-gallery-global" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="POD provider"><input className={inputCls} value={editing.pod_provider} onChange={(e) => setEditing({ ...editing, pod_provider: e.target.value })} placeholder="printful / printify" data-testid="sp-pod-provider" /></Field>
                <Field label="Provider product ID"><input className={inputCls} value={editing.provider_product_id} onChange={(e) => setEditing({ ...editing, provider_product_id: e.target.value })} data-testid="sp-provider-product-id" /></Field>
                <Field label="Provider variant ID"><input className={inputCls} value={editing.provider_variant_id} onChange={(e) => setEditing({ ...editing, provider_variant_id: e.target.value })} data-testid="sp-provider-variant-id" /></Field>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Status"><select className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="sp-status">{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
              <Field label="Display order"><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></Field>
            </div>
            <div className="my-4"><Toggle checked={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} label="Published" testId="sp-published" /></div>
            <button type="submit" data-testid="save-shop-product" className="w-full bg-[#0A0A0A] text-white rounded-md py-2.5 font-medium">Save Product</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Newsletter ---------- */
function NewsletterTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/newsletter").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  return (
    <div>
      <h2 className="text-xl font-bold text-[#0A0A0A] mb-6">Subscribers <span className="text-gray-400 font-normal">({items.length})</span></h2>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="subscribers-table">
          <thead className="bg-gray-50 text-left text-[#52525B]"><tr><th className="px-5 py-3 font-medium">Email</th><th className="px-5 py-3 font-medium">Name</th><th className="px-5 py-3 font-medium">Date</th></tr></thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={3} className="px-5 py-10 text-center text-gray-400">No subscribers yet.</td></tr> :
              items.map((s, i) => (
                <tr key={i} className="border-t border-gray-100"><td className="px-5 py-3 text-[#0A0A0A]">{s.email}</td><td className="px-5 py-3">{s.name || "—"}</td><td className="px-5 py-3 text-[#52525B]">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td></tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Gallery ---------- */
function GalleryTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const mediaRef = useRef();
  const [upBusy, setUpBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/gallery").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const blank = { image_url: "", caption: "", media_type: "image", order: items.length + 1, published: true };

  const uploadMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const mt = (data.content_type || file.type || "").startsWith("video") ? "video" : "image";
      setEditing((ed) => ({ ...ed, image_url: data.url, media_type: mt }));
      toast.success("Media uploaded");
    } catch (err) { toast.error("Upload failed"); } finally { setUpBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing.image_url) { toast.error("Please add an image first"); return; }
    const body = { ...editing, order: Number(editing.order) || 0 };
    try {
      if (editing.id) await api.put(`/admin/gallery/${editing.id}`, body);
      else await api.post("/admin/gallery", body);
      toast.success("Photo saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this photo?")) return; await api.delete(`/admin/gallery/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-[#0A0A0A]">Gallery <span className="text-gray-400 font-normal">({items.length})</span></h2>
        <button onClick={() => setEditing(blank)} data-testid="add-gallery-btn" className="bg-[#0A0A0A] text-white rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-gray-800"><Plus size={16} /> Add Photo</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`admin-gallery-${g.id}`}>
            {g.media_type === "video"
              ? <video src={imgUrl(g.image_url)} muted playsInline className="w-full h-36 object-cover bg-black" />
              : <img src={imgUrl(g.image_url)} alt={g.caption} className="w-full h-36 object-cover" />}
            <div className="p-3">
              <p className="text-xs text-[#52525B] mb-2 min-h-[2rem]">{g.caption || "No caption"}{!g.published && " · Hidden"}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing(g)} data-testid={`edit-gallery-${g.id}`} className="flex-1 border border-gray-200 rounded-md py-1.5 text-xs inline-flex items-center justify-center gap-1 hover:bg-gray-50"><Pencil size={12} /> Edit</button>
                <button onClick={() => remove(g.id)} data-testid={`delete-gallery-${g.id}`} className="border border-gray-200 rounded-md px-2.5 py-1.5 text-red-600 hover:bg-red-50"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Photo" : "Add Photo"} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            <Field label="Photo or Video">
              <div className="flex items-center gap-3 mb-2">
                <button type="button" onClick={() => mediaRef.current.click()} disabled={upBusy} data-testid="gallery-upload-btn" className="inline-flex items-center gap-2 border border-gray-300 rounded-md px-3 py-2 text-sm font-medium hover:bg-gray-50">{upBusy ? "Uploading…" : "Upload image / video"}</button>
                <input ref={mediaRef} type="file" accept="image/*,video/*" className="hidden" onChange={uploadMedia} data-testid="gallery-file-input" />
                {editing.image_url && <span className="text-xs text-green-700">Added ({editing.media_type || "image"})</span>}
              </div>
              {editing.image_url && (editing.media_type === "video"
                ? <video src={imgUrl(editing.image_url)} controls className="w-full max-h-48 rounded-md bg-black" data-testid="gallery-preview-video" />
                : <img src={imgUrl(editing.image_url)} alt="preview" className="w-24 h-24 object-cover rounded-md" />)}
              <input value={editing.image_url || ""} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="…or paste media URL" className={`${inputCls} mt-2`} data-testid="gallery-image-url" />
            </Field>
            <Field label="Caption"><input className={inputCls} value={editing.caption} onChange={(e) => setEditing({ ...editing, caption: e.target.value })} data-testid="gallery-caption" /></Field>
            <Field label="Display order"><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} data-testid="gallery-order" /></Field>
            <div className="my-4"><Toggle checked={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} label="Published" testId="gallery-published" /></div>
            <button type="submit" data-testid="save-gallery" className="w-full bg-[#0A0A0A] text-white rounded-md py-2.5 font-medium hover:bg-gray-800">Save Photo</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Contacts inbox ---------- */
const CONTACT_STATUSES = ["New", "Read", "Responded", "Follow-up Needed", "Closed", "Spam"];
const CONTACT_TYPES = ["General", "Volunteer", "Partnership", "Media", "A Better Ethiopia", "Mercy Made", "Other"];

function ContactsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/contacts").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => { try { await api.put(`/admin/contacts/${id}`, body); toast.success("Updated"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const remove = async (id) => { if (!window.confirm("Delete this message?")) return; try { await api.delete(`/admin/contacts/${id}`); toast.success("Deleted"); setOpen(null); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const openMsg = (a) => { setOpen(open === a.id ? null : a.id); if (a.status === "New" && open !== a.id) patch(a.id, { status: "Read" }); };

  const filtered = items.filter((a) => {
    if (status !== "all" && a.status !== status) return false;
    if (type !== "all" && a.inquiry_type !== type) return false;
    if (!q) return true;
    return `${a.name} ${a.email} ${a.subject} ${a.message}`.toLowerCase().includes(q.toLowerCase());
  });

  const exportCsv = () => {
    const cols = ["date", "name", "email", "phone", "inquiry_type", "subject", "message", "status", "notes"];
    const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const rows = [cols.join(","), ...filtered.map((a) => cols.map((c) => esc(a[c])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([rows], { type: "text/csv" }));
    const link = document.createElement("a"); link.href = url; link.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  const badge = (s) => ({ New: "bg-blue-100 text-blue-700", Responded: "bg-green-100 text-green-700", "Follow-up Needed": "bg-[#E0A526]/25 text-[#8a6d18]", Closed: "bg-gray-100 text-gray-500", Spam: "bg-red-100 text-red-600" }[s] || "bg-gray-100 text-gray-600");

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="text-xl font-bold text-[#0A0A0A]">Contacts <span className="text-gray-400 font-normal">({items.length})</span></h2>
        <button onClick={exportCsv} data-testid="contacts-export" className="border border-gray-200 rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-gray-50"><Download size={15} /> Export CSV</button>
      </div>
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, subject…" className={`${inputCls} pl-9`} data-testid="contacts-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} max-w-[190px]`} data-testid="contacts-filter-status">
          <option value="all">All statuses</option>{CONTACT_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} className={`${inputCls} max-w-[190px]`} data-testid="contacts-filter-type">
          <option value="all">All types</option>{CONTACT_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? <p className="text-gray-400 py-10 text-center border border-dashed border-gray-200 rounded-lg" data-testid="contacts-empty">No messages{items.length ? " match your filter" : " yet"}.</p> :
          filtered.map((a) => (
            <div key={a.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`admin-contact-${a.id}`}>
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`text-[#0A0A0A] ${a.status === "New" ? "font-bold" : "font-semibold"}`}>{a.subject || "(no subject)"}</h3>
                    <span className="text-xs px-2 py-0.5 rounded bg-[#1F4D2E]/8 text-[#1F4D2E]">{a.inquiry_type}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${badge(a.status)}`}>{a.status}</span>
                  </div>
                  <p className="text-sm text-[#52525B]">{a.name} · {a.email}{a.phone ? ` · ${a.phone}` : ""}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select value={a.status} onChange={(e) => patch(a.id, { status: e.target.value })} data-testid={`contact-status-${a.id}`} className="text-xs border border-gray-200 rounded-md px-2 py-1.5">
                    {CONTACT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <button onClick={() => openMsg(a)} data-testid={`contact-view-${a.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-sm hover:bg-gray-50">{open === a.id ? "Hide" : "View"}</button>
                  <a href={`mailto:${a.email}?subject=Re: ${encodeURIComponent(a.subject || "Your message to Refugium Ethiopia")}`} data-testid={`contact-reply-${a.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-sm hover:bg-gray-50 inline-flex items-center gap-1"><Mail size={13} /> Reply</a>
                  <button onClick={() => remove(a.id)} data-testid={`contact-delete-${a.id}`} className="border border-gray-200 rounded-md px-2.5 py-1.5 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                </div>
              </div>
              {open === a.id && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3 text-sm">
                  <p className="whitespace-pre-line text-[#0A0A0A]">{a.message}</p>
                  <div>
                    <label className="text-[#52525B] font-medium block mb-1">Internal notes</label>
                    <textarea defaultValue={a.notes || ""} rows={2} className={inputCls} data-testid={`contact-notes-${a.id}`} onBlur={(e) => e.target.value !== (a.notes || "") && patch(a.id, { notes: e.target.value })} placeholder="Private notes (saved on blur)…" />
                  </div>
                  <p className="text-xs text-gray-400">Received {a.date ? new Date(a.date).toLocaleString() : "—"}</p>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

/* ---------- A Better Ethiopia ideas ---------- */
const ABE_STATUSES = ["Submitted", "Under Review", "Needs Research", "Researching", "Promising",
  "Feasibility Study", "Seeking Partners", "Seeking Funding", "Approved for Pilot",
  "Pilot in Progress", "Active", "Completed", "On Hold", "Not Pursuing", "Archived"];

function AbeTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/abe-ideas").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => { try { await api.put(`/admin/abe-ideas/${id}`, body); toast.success("Updated"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const remove = async (id) => { if (!window.confirm("Delete this idea?")) return; try { await api.delete(`/admin/abe-ideas/${id}`); toast.success("Deleted"); setOpen(null); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };

  const filtered = items.filter((a) => {
    if (filter !== "all" && a.status !== filter) return false;
    if (!q) return true;
    return `${a.name} ${a.email} ${a.category} ${a.region} ${a.city} ${a.idea} ${a.challenge}`.toLowerCase().includes(q.toLowerCase());
  });

  const exportCsv = () => {
    const cols = ["date", "name", "email", "phone", "category", "region", "city", "nationwide", "challenge", "idea", "benefit", "organization", "profession", "expertise", "participation", "status", "public", "notes"];
    const esc = (v) => `"${String(v == null ? "" : Array.isArray(v) ? v.join("; ") : v).replace(/"/g, '""')}"`;
    const rows = [cols.join(","), ...filtered.map((a) => cols.map((c) => esc(a[c])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([rows], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url; link.download = `abe-ideas-${new Date().toISOString().slice(0, 10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="text-xl font-bold text-[#0A0A0A]">A Better Ethiopia — Ideas <span className="text-gray-400 font-normal">({items.length})</span></h2>
        <button onClick={exportCsv} data-testid="abe-export" className="border border-gray-200 rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-gray-50"><Download size={15} /> Export CSV</button>
      </div>
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, category, idea…" className={`${inputCls} pl-9`} data-testid="abe-search" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${inputCls} max-w-[220px]`} data-testid="abe-filter">
          <option value="all">All statuses</option>
          {ABE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? <p className="text-gray-400 py-10 text-center border border-dashed border-gray-200 rounded-lg" data-testid="abe-empty">No ideas{items.length ? " match your filter" : " yet"}.</p> :
          filtered.map((a) => (
            <div key={a.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`admin-abe-${a.id}`}>
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-[#0A0A0A]">{a.category || "Idea"} — {a.name}</h3>
                    <span className="text-xs px-2 py-0.5 rounded bg-[#E0A526]/20 text-[#8a6d18]">{a.status}</span>
                    {a.public && <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Public</span>}
                  </div>
                  <p className="text-sm text-[#52525B]">{a.email}{a.region ? ` · ${a.region}` : ""}{a.city ? `, ${a.city}` : ""}{a.nationwide ? " · Nationwide" : ""}</p>
                  <p className="text-xs text-[#52525B] mt-1 line-clamp-1">{a.idea}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select value={a.status} onChange={(e) => patch(a.id, { status: e.target.value })} data-testid={`abe-status-${a.id}`} className="text-xs border border-gray-200 rounded-md px-2 py-1.5">
                    {ABE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => setOpen(open === a.id ? null : a.id)} data-testid={`abe-view-${a.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-sm hover:bg-gray-50">{open === a.id ? "Hide" : "View"}</button>
                  <button onClick={() => remove(a.id)} data-testid={`abe-delete-${a.id}`} className="border border-gray-200 rounded-md px-2.5 py-1.5 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                </div>
              </div>
              {open === a.id && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-sm">
                  {a.challenge && <p><span className="text-[#52525B] font-medium">Challenge:</span> {a.challenge}</p>}
                  <p><span className="text-[#52525B] font-medium">Idea:</span> {a.idea}</p>
                  {a.benefit && <p><span className="text-[#52525B] font-medium">How it helps:</span> {a.benefit}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                    {[["Phone / WhatsApp", a.phone], ["Organization", a.organization], ["Profession", a.profession], ["Expertise", a.expertise]].filter(([, v]) => v).map(([k, v]) => <p key={k}><span className="text-[#52525B] font-medium">{k}:</span> {v}</p>)}
                  </div>
                  {(a.participation || []).length > 0 && <p><span className="text-[#52525B] font-medium">Participation:</span> {a.participation.join(", ")}</p>}
                  <label className="flex items-center gap-2 pt-1"><input type="checkbox" checked={!!a.public} onChange={(e) => patch(a.id, { public: e.target.checked })} data-testid={`abe-public-${a.id}`} className="w-4 h-4" /> <span className="text-[#52525B]">Show publicly (future feature)</span></label>
                  <div>
                    <label className="text-[#52525B] font-medium block mb-1">Internal notes</label>
                    <textarea defaultValue={a.notes || ""} rows={2} className={inputCls} data-testid={`abe-notes-${a.id}`} onBlur={(e) => e.target.value !== (a.notes || "") && patch(a.id, { notes: e.target.value })} placeholder="Private notes (saved on blur)…" />
                  </div>
                  <p className="text-xs text-gray-400">Submitted {a.date ? new Date(a.date).toLocaleString() : "—"}</p>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

/* ---------- Community Impact Projects ---------- */
const CI_FOCUS = ["Water & Sanitation", "Education & Schools", "Agriculture & Food Security", "Technology & Connectivity", "Clean Energy", "Community Development"];
const CI_STATUS = ["planning", "in_progress", "completed"];

function CommunityTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/community-projects").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const blank = { title: "", focus_area: "Community Development", location: "", summary: "", description: "", image_url: "", status: "planning", goal: 0, raised: 0, progress: 0, gofundme_url: "", updates: [], featured: false, order: items.length + 1, published: true };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...editing, goal: Number(editing.goal) || 0, raised: Number(editing.raised) || 0, progress: Number(editing.progress) || 0, order: Number(editing.order) || 0, updates: (editing.updates || []).filter((u) => u.title || u.body) };
    try {
      if (editing.id) await api.put(`/admin/community-projects/${editing.id}`, body);
      else await api.post("/admin/community-projects", body);
      toast.success("Project saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this project?")) return; try { await api.delete(`/admin/community-projects/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const addUpdate = () => setEditing((ed) => ({ ...ed, updates: [...(ed.updates || []), { date: new Date().toISOString().slice(0, 10), title: "", body: "" }] }));
  const setUpdate = (i, k, v) => setEditing((ed) => { const up = [...(ed.updates || [])]; up[i] = { ...up[i], [k]: v }; return { ...ed, updates: up }; });
  const rmUpdate = (i) => setEditing((ed) => ({ ...ed, updates: (ed.updates || []).filter((_, x) => x !== i) }));

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-[#0A0A0A]">Community Impact Projects <span className="text-gray-400 font-normal">({items.length})</span></h2>
        <button onClick={() => setEditing(blank)} data-testid="add-community-btn" className="bg-[#0A0A0A] text-white rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-gray-800"><Plus size={16} /> New Project</button>
      </div>
      <p className="text-sm text-[#52525B] mb-4">Publish a project only once real work begins — it appears on the public Community Impact page with progress updates.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 ? <p className="text-gray-400 py-10 text-center border border-dashed border-gray-200 rounded-lg col-span-full" data-testid="admin-community-empty">No community projects yet.</p> :
          items.map((p) => (
            <div key={p.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`admin-community-${p.id}`}>
              {p.image_url && <img src={imgUrl(p.image_url)} alt={p.title} className="w-full h-32 object-cover" />}
              <div className="p-4">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#52525B]">{p.focus_area}</span>
                  {!p.published && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Hidden</span>}
                </div>
                <h3 className="font-semibold text-[#0A0A0A]">{p.title}</h3>
                <p className="text-xs text-[#52525B] mb-2">{p.status.replace(/_/g, " ")}{p.goal > 0 ? ` · ${money(p.raised)}/${money(p.goal)}` : p.progress ? ` · ${p.progress}%` : ""} · {(p.updates || []).length} update(s)</p>
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ ...p, updates: p.updates || [] })} data-testid={`edit-community-${p.id}`} className="flex-1 border border-gray-200 rounded-md py-1.5 text-xs inline-flex items-center justify-center gap-1 hover:bg-gray-50"><Pencil size={12} /> Edit</button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-community-${p.id}`} className="border border-gray-200 rounded-md px-2.5 py-1.5 text-red-600 hover:bg-red-50"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Community Project" : "New Community Project"} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            <Field label="Title"><input required className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="community-title" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Focus area"><select className={inputCls} value={editing.focus_area} onChange={(e) => setEditing({ ...editing, focus_area: e.target.value })} data-testid="community-focus">{CI_FOCUS.map((c) => <option key={c}>{c}</option>)}</select></Field>
              <Field label="Status"><select className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="community-status">{CI_STATUS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}</select></Field>
            </div>
            <Field label="Location"><input className={inputCls} value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} placeholder="e.g. Amhara Region" data-testid="community-location" /></Field>
            <Field label="Short summary (shown on card)"><textarea className={inputCls} rows={2} value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} data-testid="community-summary" /></Field>
            <Field label="Full description (optional)"><textarea className={inputCls} rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="community-description" /></Field>
            <Field label="Image"><ImageUpload value={editing.image_url} onChange={(url) => setEditing({ ...editing, image_url: url })} testId="community-image" /></Field>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Goal ($) — optional"><input type="number" className={inputCls} value={editing.goal} onChange={(e) => setEditing({ ...editing, goal: e.target.value })} data-testid="community-goal" /></Field>
              <Field label="Raised ($) — optional"><input type="number" className={inputCls} value={editing.raised} onChange={(e) => setEditing({ ...editing, raised: e.target.value })} data-testid="community-raised" /></Field>
              <Field label="Progress % (if no goal)"><input type="number" min="0" max="100" className={inputCls} value={editing.progress} onChange={(e) => setEditing({ ...editing, progress: e.target.value })} data-testid="community-progress" /></Field>
            </div>
            <Field label="GoFundMe URL (optional — defaults to global)"><input className={inputCls} value={editing.gofundme_url} onChange={(e) => setEditing({ ...editing, gofundme_url: e.target.value })} placeholder="https://gofund.me/..." data-testid="community-gofundme" /></Field>

            <div className="border border-gray-100 rounded-md p-3 my-4">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-[#0A0A0A]">Progress updates</label>
                <button type="button" onClick={addUpdate} data-testid="community-add-update" className="text-xs border border-gray-200 rounded-md px-2.5 py-1 inline-flex items-center gap-1 hover:bg-gray-50"><Plus size={12} /> Add update</button>
              </div>
              {(editing.updates || []).length === 0 && <p className="text-xs text-gray-400">No updates yet. Add milestones so supporters can follow real progress.</p>}
              {(editing.updates || []).map((u, i) => (
                <div key={i} className="border border-gray-100 rounded-md p-2.5 mb-2" data-testid={`community-update-${i}`}>
                  <div className="flex gap-2 mb-2">
                    <input type="date" className={`${inputCls} max-w-[160px]`} value={(u.date || "").slice(0, 10)} onChange={(e) => setUpdate(i, "date", e.target.value)} data-testid={`community-update-date-${i}`} />
                    <input className={inputCls} placeholder="Update title" value={u.title || ""} onChange={(e) => setUpdate(i, "title", e.target.value)} data-testid={`community-update-title-${i}`} />
                    <button type="button" onClick={() => rmUpdate(i)} className="text-red-600 px-2" data-testid={`community-update-remove-${i}`}><Trash2 size={14} /></button>
                  </div>
                  <textarea className={inputCls} rows={2} placeholder="What happened / what's next" value={u.body || ""} onChange={(e) => setUpdate(i, "body", e.target.value)} data-testid={`community-update-body-${i}`} />
                </div>
              ))}
            </div>

            <Field label="Display order"><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} data-testid="community-order" /></Field>
            <div className="my-4"><Toggle checked={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} label="Published" testId="community-published" /></div>
            <button type="submit" data-testid="save-community" className="w-full bg-[#0A0A0A] text-white rounded-md py-2.5 font-medium hover:bg-gray-800">Save Project</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Founding Network Applications ---------- */
const APP_STATUSES = ["New", "Under Review", "Contacted", "Meeting Scheduled", "Board Candidate",
  "Advisor Candidate", "Ambassador Candidate", "Partnership Candidate", "Accepted", "Not Selected", "Archived"];

function ApplicationsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/founding-applications").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => { try { await api.put(`/admin/founding-applications/${id}`, { status }); toast.success("Status updated"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const saveNotes = async (id, notes) => { try { await api.put(`/admin/founding-applications/${id}`, { notes }); toast.success("Notes saved"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const remove = async (id) => { if (!window.confirm("Delete this application?")) return; try { await api.delete(`/admin/founding-applications/${id}`); toast.success("Deleted"); setOpen(null); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };

  const filtered = items.filter((a) => {
    if (filter !== "all" && a.status !== filter) return false;
    if (!q) return true;
    const hay = `${a.name} ${a.email} ${a.country} ${a.profession} ${a.organization} ${(a.interests || []).join(" ")}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const exportCsv = () => {
    const cols = ["date", "name", "email", "phone", "city", "country", "profession", "current_role", "organization", "interests", "why", "contribution", "time_commitment", "video_available", "linkedin", "website", "social", "cv_url", "status", "notes"];
    const esc = (v) => `"${String(v == null ? "" : Array.isArray(v) ? v.join("; ") : v).replace(/"/g, '""')}"`;
    const rows = [cols.join(","), ...filtered.map((a) => cols.map((c) => esc(a[c])).join(","))].join("\n");
    const blob = new Blob([rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `founding-applications-${new Date().toISOString().slice(0, 10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  const badge = (s) => {
    const map = { New: "bg-blue-100 text-blue-700", Accepted: "bg-green-100 text-green-700", "Not Selected": "bg-gray-100 text-gray-500", Archived: "bg-gray-100 text-gray-400" };
    return map[s] || "bg-[#E0A526]/20 text-[#8a6d18]";
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="text-xl font-bold text-[#0A0A0A]">Founding Network Applications <span className="text-gray-400 font-normal">({items.length})</span></h2>
        <button onClick={exportCsv} data-testid="applications-export" className="border border-gray-200 rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-gray-50"><Download size={15} /> Export CSV</button>
      </div>
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, country, expertise…" className={`${inputCls} pl-9`} data-testid="applications-search" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${inputCls} max-w-[220px]`} data-testid="applications-filter">
          <option value="all">All statuses</option>
          {APP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? <p className="text-gray-400 py-10 text-center border border-dashed border-gray-200 rounded-lg" data-testid="applications-empty">No applications{items.length ? " match your filter" : " yet"}.</p> :
          filtered.map((a) => (
            <div key={a.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`admin-application-${a.id}`}>
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-[#0A0A0A]">{a.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded ${badge(a.status)}`}>{a.status}</span>
                  </div>
                  <p className="text-sm text-[#52525B]">{a.email}{a.country ? ` · ${a.country}` : ""}{a.profession ? ` · ${a.profession}` : ""}</p>
                  <p className="text-xs text-[#52525B] mt-1">{(a.interests || []).slice(0, 4).join(" · ")}{(a.interests || []).length > 4 ? " …" : ""}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select value={a.status} onChange={(e) => setStatus(a.id, e.target.value)} data-testid={`application-status-${a.id}`} className="text-xs border border-gray-200 rounded-md px-2 py-1.5">
                    {APP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => setOpen(open === a.id ? null : a.id)} data-testid={`application-view-${a.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-sm hover:bg-gray-50">{open === a.id ? "Hide" : "View"}</button>
                  <button onClick={() => remove(a.id)} data-testid={`application-delete-${a.id}`} className="border border-gray-200 rounded-md px-2.5 py-1.5 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                </div>
              </div>
              {open === a.id && (
                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {[["Phone / WhatsApp", a.phone], ["City", a.city], ["Current role", a.current_role], ["Organization", a.organization], ["Time available", a.time_commitment], ["Video meeting", a.video_available], ["LinkedIn", a.linkedin], ["Website", a.website], ["Social", a.social]].filter(([, v]) => v).map(([k, v]) => (
                    <p key={k}><span className="text-[#52525B] font-medium">{k}:</span> {v}</p>
                  ))}
                  <p className="sm:col-span-2"><span className="text-[#52525B] font-medium">Interested in:</span> {(a.interests || []).join(", ") || "—"}</p>
                  {a.why && <p className="sm:col-span-2"><span className="text-[#52525B] font-medium">Why involved:</span> {a.why}</p>}
                  {a.contribution && <p className="sm:col-span-2"><span className="text-[#52525B] font-medium">Can contribute:</span> {a.contribution}</p>}
                  {a.cv_url && <p className="sm:col-span-2"><a href={imgUrl(a.cv_url)} target="_blank" rel="noopener noreferrer" className="text-[#C1272D] font-medium underline" data-testid={`application-cv-${a.id}`}>Download CV / Resume</a></p>}
                  <div className="sm:col-span-2 mt-2">
                    <label className="text-[#52525B] font-medium block mb-1">Internal notes</label>
                    <textarea defaultValue={a.notes || ""} rows={2} className={inputCls} data-testid={`application-notes-${a.id}`} onBlur={(e) => e.target.value !== (a.notes || "") && saveNotes(a.id, e.target.value)} placeholder="Add private notes (saved on blur)…" />
                  </div>
                  <p className="sm:col-span-2 text-xs text-gray-400">Submitted {a.date ? new Date(a.date).toLocaleString() : "—"}</p>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

function AdminStatCard({ label, value, hint, onClick }) {
  return (
    <div onClick={onClick} className={`bg-white border border-gray-200 rounded-xl p-5 ${onClick ? "cursor-pointer hover:border-[#0A0A0A] transition-colors" : ""}`} data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
      <p className="text-3xl font-bold text-[#0A0A0A]">{value}</p>
      <p className="text-sm text-[#52525B] mt-1">{label}</p>
      {hint ? <p className="text-xs text-[#C1272D] font-medium mt-1">{hint}</p> : null}
    </div>
  );
}

function OverviewTab({ setTab }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  const cards = s ? [
    ["Members", s.members, s.members_this_month ? `+${s.members_this_month} this month` : null, "applications"],
    ["Newsletter subscribers", s.newsletter, null, "newsletter"],
    ["New contact messages", s.contacts, s.contacts_new ? `${s.contacts_new} new` : null, "contacts"],
    ["Founding applications", s.founding_applications, s.founding_new ? `${s.founding_new} new` : null, "applications"],
    ["A Better Ethiopia ideas", s.abe_ideas, s.abe_new ? `${s.abe_new} new` : null, "abe"],
    ["Community projects", s.community_projects, null, "community"],
    ["Programs (rooms)", s.programs, null, "projects"],
    ["Total raised", money(s.total_raised), null, "projects"],
    ["Supporters", s.supporters, null, "supporters"],
    ["Published stories", s.stories_published, null, "stories"],
    ["Draft stories", s.stories_draft, null, "stories"],
    ["Shop orders", s.shop_orders, null, "shop"],
    ["Employees", s.employees, null, "employees"],
    ["Volunteer applications", s.volunteer_apps, s.volunteer_pending ? `${s.volunteer_pending} pending` : null, "volunteers"],
  ] : [];
  const actions = [
    ["Review applications", "applications"], ["Review volunteers", "volunteers"], ["Add a program", "projects"],
    ["Publish a story", "stories"], ["Security log", "security"], ["Site settings", "settings"],
  ];
  return (
    <div data-testid="overview-tab">
      <h2 className="text-xl font-bold text-[#0A0A0A] mb-1">Welcome back</h2>
      <p className="text-sm text-[#52525B] mb-6">Here's what's happening across Refugium Ethiopia.</p>
      {!s ? <p className="text-gray-400 py-10 text-center" data-testid="overview-loading">Loading…</p> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {cards.map(([l, v, h, go]) => <AdminStatCard key={l} label={l} value={v} hint={h} onClick={() => setTab(go)} />)}
        </div>
      )}
      <h3 className="text-sm font-bold uppercase tracking-wider text-[#52525B] mt-10 mb-3">Quick actions</h3>
      <div className="flex flex-wrap gap-3">
        {actions.map(([label, id]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`quick-${id}`} className="bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-medium hover:border-[#0A0A0A] transition-colors">{label}</button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Employees / Volunteers / Security (super admin) ---------- */
function EmployeesTab() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/employees").then(({ data }) => setList(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const togglePerm = (p) => setEditing((e) => ({ ...e, permissions: e.permissions.includes(p) ? e.permissions.filter((x) => x !== p) : [...e.permissions, p] }));
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await api.patch(`/admin/employees/${editing.id}`, { name: editing.name, permissions: editing.permissions, status: editing.status });
      else { await api.post("/admin/employees", { email: editing.email, name: editing.name, permissions: editing.permissions }); toast.success("Employee invited — a password-setup email was sent"); }
      setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const resetPw = async (id) => { await api.post(`/admin/employees/${id}/reset-password`); toast.success("Password reset link sent"); };
  const reset2fa = async (id) => { if (!window.confirm("Reset this user's 2FA? They must re-enroll on next login.")) return; await api.post(`/admin/users/${id}/2fa/reset`); toast.success("2FA reset"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this employee?")) return; await api.delete(`/admin/employees/${id}`); toast.success("Deleted"); load(); };
  const toggleStatus = async (emp) => { await api.patch(`/admin/employees/${emp.id}`, { status: emp.status === "suspended" ? "active" : "suspended" }); load(); };
  return (
    <div data-testid="employees-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[#52525B]">Employees are created and permissioned by a Super Admin. They set their own password and must enroll 2FA before access.</p>
        <button onClick={() => setEditing({ email: "", name: "", permissions: [] })} data-testid="add-employee" className="bg-[#0A0A0A] text-white rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 shrink-0"><Plus size={16} /> New Employee</button>
      </div>
      <div className="space-y-3">
        {list.length === 0 ? <p className="text-gray-400 py-10 text-center border border-dashed border-gray-200 rounded-lg" data-testid="employees-empty">No employees yet.</p> :
          list.map((emp) => (
            <div key={emp.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`employee-${emp.id}`}>
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                  <p className="font-semibold text-sm">{emp.name} <span className="text-[#52525B] font-normal">· {emp.email}</span></p>
                  <p className="text-xs text-[#52525B] mt-0.5">
                    <span className={`px-1.5 py-0.5 rounded ${emp.status === "suspended" ? "bg-red-100 text-red-700" : emp.status === "invited" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{emp.status}</span>
                    <span className="ml-2">2FA: {emp.mfa_enabled ? "on" : "not set"}</span>
                    <span className="ml-2">{emp.permissions.length} permission(s)</span>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setEditing({ ...emp })} data-testid={`edit-employee-${emp.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1"><Pencil size={12} /> Edit</button>
                  <button onClick={() => toggleStatus(emp)} data-testid={`suspend-employee-${emp.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-xs">{emp.status === "suspended" ? "Reactivate" : "Suspend"}</button>
                  <button onClick={() => resetPw(emp.id)} className="border border-gray-200 rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1"><KeyRound size={12} /> Reset password</button>
                  <button onClick={() => reset2fa(emp.id)} className="border border-gray-200 rounded-md px-3 py-1.5 text-xs">Reset 2FA</button>
                  <button onClick={() => del(emp.id)} className="border border-gray-200 rounded-md px-2.5 py-1.5 text-red-600"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
      </div>
      {editing && (
        <Modal title={editing.id ? "Edit Employee" : "New Employee"} onClose={() => setEditing(null)}>
          <form onSubmit={save}>
            {!editing.id && <Field label="Email"><input required type="email" className={inputCls} value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="emp-email" /></Field>}
            <Field label="Name"><input className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="emp-name" /></Field>
            {editing.id && <Field label="Status"><select className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}><option value="active">active</option><option value="invited">invited</option><option value="suspended">suspended</option></select></Field>}
            <p className="text-sm font-medium text-[#0A0A0A] mb-2">Permissions</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {EMP_PERMS.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.permissions.includes(k)} onChange={() => togglePerm(k)} data-testid={`perm-${k}`} /> {l}</label>
              ))}
            </div>
            <button type="submit" data-testid="save-employee" className="w-full bg-[#0A0A0A] text-white rounded-md py-2.5 font-medium">{editing.id ? "Save changes" : "Invite employee"}</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function VolunteersTab() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [volunteers, setVolunteers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/volunteer-applications").then(({ data }) => setList(data)).catch(() => {});
    api.get("/admin/volunteers").then(({ data }) => setVolunteers(data)).catch(() => {});
    api.get("/admin/assignments").then(({ data }) => setAssignments(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => { try { await api.post(`/admin/volunteer-applications/${id}/approve`); toast.success("Approved — invitation sent"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const decline = async (id) => { await api.post(`/admin/volunteer-applications/${id}/decline`); toast.success("Declined"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this application?")) return; await api.delete(`/admin/volunteer-applications/${id}`); load(); };
  const saveAssignment = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/assignments", assignFor); toast.success("Assignment created"); setAssignFor(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setAStatus = async (aid, status) => { await api.patch(`/admin/assignments/${aid}`, { status }); load(); };
  const delA = async (aid) => { if (!window.confirm("Delete this assignment?")) return; await api.delete(`/admin/assignments/${aid}`); load(); };
  const shown = filter === "all" ? list : list.filter((v) => v.status === filter);
  const badge = { pending: "bg-amber-100 text-amber-700", approved: "bg-green-100 text-green-700", declined: "bg-red-100 text-red-700" };
  return (
    <div data-testid="volunteers-tab">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex gap-2">
          {["all", "pending", "approved", "declined"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`vol-filter-${f}`} className={`px-3 py-1.5 rounded-md text-sm ${filter === f ? "bg-[#0A0A0A] text-white" : "border border-gray-200"}`}>{f}</button>
          ))}
        </div>
        <button onClick={() => setAssignFor({ volunteer_id: volunteers[0]?.id || "", title: "", details: "", due_date: "", status: "assigned" })} disabled={volunteers.length === 0} data-testid="new-assignment" className="bg-[#1F4D2E] text-white rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-40"><Plus size={16} /> Assign task</button>
      </div>

      <div className="space-y-3 mb-8">
        {shown.length === 0 ? <p className="text-gray-400 py-10 text-center border border-dashed border-gray-200 rounded-lg" data-testid="volunteers-empty">No applications.</p> :
          shown.map((v) => (
            <div key={v.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`vol-app-${v.id}`}>
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                  <p className="font-semibold text-sm">{v.name} <span className="font-normal text-[#52525B]">· {v.email}</span> <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${badge[v.status] || "bg-gray-100"}`}>{v.status}</span></p>
                  <p className="text-xs text-[#52525B] mt-1">{v.location} · {v.interests} · {v.availability}</p>
                  {v.message && <p className="text-sm text-[#3f3f46] mt-1">{v.message}</p>}
                </div>
                <div className="flex gap-2">
                  {v.status === "pending" && <button onClick={() => approve(v.id)} data-testid={`approve-vol-${v.id}`} className="bg-[#1F4D2E] text-white rounded-md px-3 py-1.5 text-xs">Approve</button>}
                  {v.status === "pending" && <button onClick={() => decline(v.id)} data-testid={`decline-vol-${v.id}`} className="border border-gray-200 rounded-md px-3 py-1.5 text-xs">Decline</button>}
                  <button onClick={() => del(v.id)} className="border border-gray-200 rounded-md px-2.5 py-1.5 text-red-600"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
      </div>

      <h3 className="text-sm font-bold uppercase tracking-wide text-[#0A0A0A] mb-3">Assignments</h3>
      <div className="space-y-3" data-testid="assignments-list">
        {assignments.length === 0 ? <p className="text-gray-400 py-8 text-center border border-dashed border-gray-200 rounded-lg" data-testid="assignments-empty">No assignments yet. Approve a volunteer, then assign a task.</p> :
          assignments.map((a) => (
            <div key={a.id} className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap justify-between items-start gap-3" data-testid={`assignment-${a.id}`}>
              <div>
                <p className="font-semibold text-sm">{a.title} <span className="font-normal text-[#52525B]">· {a.volunteer_name}</span></p>
                {a.details && <p className="text-xs text-[#52525B] mt-1">{a.details}</p>}
                {a.due_date && <p className="text-xs text-[#52525B] mt-0.5">Due: {a.due_date}</p>}
              </div>
              <div className="flex items-center gap-2">
                <select value={a.status} onChange={(e) => setAStatus(a.id, e.target.value)} className="text-xs border border-gray-200 rounded-md px-2 py-1.5"><option value="assigned">assigned</option><option value="in_progress">in progress</option><option value="completed">completed</option></select>
                <button onClick={() => delA(a.id)} className="border border-gray-200 rounded-md px-2.5 py-1.5 text-red-600"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
      </div>

      {assignFor && (
        <Modal title="Assign a task" onClose={() => setAssignFor(null)}>
          <form onSubmit={saveAssignment}>
            <Field label="Volunteer"><select required className={inputCls} value={assignFor.volunteer_id} onChange={(e) => setAssignFor({ ...assignFor, volunteer_id: e.target.value })} data-testid="assign-volunteer">{volunteers.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.email})</option>)}</select></Field>
            <Field label="Task title"><input required className={inputCls} value={assignFor.title} onChange={(e) => setAssignFor({ ...assignFor, title: e.target.value })} data-testid="assign-title" /></Field>
            <Field label="Details"><textarea className={inputCls} rows={3} value={assignFor.details} onChange={(e) => setAssignFor({ ...assignFor, details: e.target.value })} data-testid="assign-details" /></Field>
            <Field label="Due date"><input className={inputCls} type="date" value={assignFor.due_date} onChange={(e) => setAssignFor({ ...assignFor, due_date: e.target.value })} data-testid="assign-due" /></Field>
            <button type="submit" data-testid="save-assignment" className="w-full bg-[#1F4D2E] text-white rounded-md py-2.5 font-medium">Create assignment</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function SecurityTab() {
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/security-events").then(({ data }) => setEvents(data)).catch(() => {}); }, []);
  const shown = events.filter((e) => !q || JSON.stringify(e).toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="security-tab">
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-sm"><Search size={15} className="absolute left-3 top-2.5 text-gray-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events…" data-testid="security-search" className="w-full border border-gray-200 rounded-md pl-9 pr-3 py-2 text-sm" /></div>
        <span className="text-sm text-[#52525B]">{shown.length} events</span>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-[#52525B]"><tr><th className="px-4 py-2">Event</th><th className="px-4 py-2">Actor</th><th className="px-4 py-2">Target</th><th className="px-4 py-2">When</th></tr></thead>
          <tbody data-testid="security-events">
            {shown.length === 0 ? <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No events.</td></tr> :
              shown.slice(0, 300).map((e, i) => (
                <tr key={i} className="border-t border-gray-100"><td className="px-4 py-2 font-medium">{e.event}</td><td className="px-4 py-2 text-[#52525B]">{e.actor || "—"}</td><td className="px-4 py-2 text-[#52525B]">{e.target || "—"}</td><td className="px-4 py-2 text-[#52525B]">{e.at ? new Date(e.at).toLocaleString() : "—"}</td></tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const visibleTabs = TABS.filter((t) => {
    if (t.id === "overview") return true;
    if (user?.role === "super_admin") return true;
    if (t.sa) return false;
    if (t.perm) return (user?.permissions || []).includes(t.perm);
    return false;
  });
  const [tab, setTab] = useState("overview");
  const activeTab = visibleTabs.some((t) => t.id === tab) ? tab : "overview";

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-admin" data-testid="admin-dashboard">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-[#C1272D] flex items-center justify-center text-white"><Heart size={16} fill="white" /></span>
            <span className="font-bold text-[#0A0A0A]">Refugium {user?.role === "super_admin" ? "Super Admin" : "Team"}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#52525B] hidden sm:inline" data-testid="admin-user-email">{user?.email}</span>
            <Link to="/" target="_blank" className="text-sm text-[#52525B] hover:text-black inline-flex items-center gap-1" data-testid="view-site"><ExternalLink size={14} /> View site</Link>
            <button onClick={logout} data-testid="logout-btn" className="text-sm text-red-600 inline-flex items-center gap-1 hover:text-red-700"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-8 overflow-x-auto hide-scrollbar border-b border-gray-200">
          {visibleTabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`px-4 py-3 text-sm font-medium inline-flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${activeTab === t.id ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#52525B] hover:text-black"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {activeTab === "overview" && <OverviewTab setTab={setTab} />}
        {activeTab === "projects" && <ProjectsTab />}
        {activeTab === "stories" && <StoriesTab />}
        {activeTab === "transparency" && <TransparencyTab />}
        {activeTab === "supporters" && <SupportersTab />}
        {activeTab === "gallery" && <GalleryTab />}
        {activeTab === "mercymade" && <MercyMadeTab />}
        {activeTab === "shop" && <ShopTab />}
        {activeTab === "community" && <CommunityTab />}
        {activeTab === "applications" && <ApplicationsTab />}
        {activeTab === "contacts" && <ContactsTab />}
        {activeTab === "abe" && <AbeTab />}
        {activeTab === "volunteers" && <VolunteersTab />}
        {activeTab === "employees" && <EmployeesTab />}
        {activeTab === "security" && <SecurityTab />}
        {activeTab === "settings" && <SettingsTab />}
        {activeTab === "newsletter" && <NewsletterTab />}
      </div>
    </div>
  );
}
